import { z } from 'zod';
import { DECK_IDS } from './decks.js';
import { MAX_QUEUE_LENGTH, type Command, type ErrorCode } from './domain.js';
import { MAX_NOTE_LENGTH, type RetroCommand, type RetroErrorCode } from './retro/domain.js';
import { MAX_NAME_LENGTH, MAX_TITLE_LENGTH } from './seats.js';
import { ROOM_CODE_LENGTH } from './ids.js';
import type { PublicRoom } from './view.js';
import type { PublicRetroRoom } from './retro/view.js';

export const PROTOCOL_VERSION = 1;

export const roleSchema = z.enum(['player', 'observer']);
export const deckIdSchema = z.enum(DECK_IDS);
export const roomIdSchema = z.string().length(ROOM_CODE_LENGTH);
export const nameSchema = z.string().min(1).max(MAX_NAME_LENGTH);
export const titleSchema = z.string().max(MAX_TITLE_LENGTH);
export const retroModeSchema = z.enum(['anonymous', 'visible']);
export const columnIdSchema = z.enum(['wentWell', 'toImprove', 'actionItems']);
export const noteTextSchema = z.string().min(1).max(MAX_NOTE_LENGTH);
const idRefSchema = z.string().min(1).max(64);

/**
 * Defined once and shared by both reducers' unions below. Referencing the same
 * schema object from two arrays is fine; what is not fine is letting it reach
 * `clientCommandSchema`'s flattened array twice — see the note there.
 */
const revealSchema = z.object({ type: z.literal('reveal') });

/** Commands with identical payload and meaning in both room kinds. */
export const seatCommandSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('setRole'), role: roleSchema }),
  z.object({ type: z.literal('setTitle'), title: titleSchema }),
  z.object({ type: z.literal('leave') }),
  z.object({ type: z.literal('transferHost'), participantId: idRefSchema }),
  z.object({ type: z.literal('removeParticipant'), participantId: idRefSchema }),
]);

export const planningCommandSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('castVote'), value: z.string().min(1).max(16) }),
  z.object({ type: z.literal('retractVote') }),
  revealSchema,
  z.object({ type: z.literal('reset') }),
  z.object({ type: z.literal('nextRound'), title: titleSchema }),
  z.object({ type: z.literal('setDeck'), deckId: deckIdSchema }),
  // Bounded here as well as in the reducer so an absurd paste is refused before
  // it is parsed. One over the queue limit, because the first pasted story can
  // go straight onto an empty round, leaving a full queue behind it — past that
  // the reducer answers with `queue_full`, which says something useful.
  z.object({ type: z.literal('queueStories'), titles: z.array(titleSchema).min(1).max(MAX_QUEUE_LENGTH + 1) }),
  z.object({ type: z.literal('removeQueuedStory'), storyId: idRefSchema }),
  z.object({ type: z.literal('setAutoReveal'), enabled: z.boolean() }),
]);

export const retroCommandSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('addNote'), column: columnIdSchema, text: noteTextSchema }),
  z.object({ type: z.literal('editNote'), noteId: idRefSchema, text: noteTextSchema }),
  z.object({ type: z.literal('removeNote'), noteId: idRefSchema }),
  revealSchema,
  z.object({ type: z.literal('unreveal') }),
  z.object({ type: z.literal('combineNotes'), noteId: idRefSchema, intoNoteId: idRefSchema }),
  z.object({ type: z.literal('unmergeNote'), noteId: idRefSchema }),
  z.object({ type: z.literal('voteNote'), noteId: idRefSchema }),
  z.object({ type: z.literal('unvoteNote'), noteId: idRefSchema }),
  z.object({ type: z.literal('setMode'), mode: retroModeSchema }),
]);

/**
 * The full set a client may send. `join` and `disconnect` are driven by the
 * transport, so they are deliberately absent from every union above.
 *
 * `reveal` appears in both planningCommandSchema and retroCommandSchema with the
 * identical shape, so it is filtered out of both sides and added back exactly
 * once here. z.discriminatedUnion throws at *construction* time on a duplicate
 * discriminant value — that is a module-load crash for every importer of this
 * package, not a test failure — and it is not a case Zod merges for you. Do not
 * "simplify" this back to a plain three-way spread.
 */
const isReveal = (option: { shape: { type: { value: unknown } } }): boolean => option.shape.type.value === 'reveal';

export const clientCommandSchema = z.discriminatedUnion('type', [
  ...seatCommandSchema.options,
  ...planningCommandSchema.options.filter((option) => !isReveal(option)),
  revealSchema,
  ...retroCommandSchema.options.filter((option) => !isReveal(option)),
]);

export type ClientCommand = z.infer<typeof clientCommandSchema>;

/** Compile-time proof that every client command is also a domain command of one kind or the other. */
type AssertAssignable<T extends U, U> = T;
export type ClientCommandIsDomainCommand = AssertAssignable<ClientCommand, Command | RetroCommand>;

export const clientMessageSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('hello'),
    roomId: roomIdSchema,
    name: nameSchema,
    role: roleSchema,
    /**
     * Secret handed out by a previous `welcome`. Lets a refresh reclaim the same
     * seat. Kept distinct from the public participant id, which every client can
     * see and must therefore never be enough to claim a seat.
     */
    token: z.string().min(16).max(128).optional(),
  }),
  z.object({ type: z.literal('command'), command: clientCommandSchema }),
  z.object({ type: z.literal('ping') }),
]);

export type ClientMessage = z.infer<typeof clientMessageSchema>;

export type ServerMessage =
  | {
      readonly type: 'welcome';
      readonly participantId: string;
      /** Store this to reclaim the seat after a reload; never share it. */
      readonly token: string;
      readonly room: PublicRoom | PublicRetroRoom;
    }
  | { readonly type: 'state'; readonly room: PublicRoom | PublicRetroRoom }
  | { readonly type: 'error'; readonly code: ErrorCode | RetroErrorCode | ProtocolErrorCode; readonly message: string }
  | { readonly type: 'pong' };

export type ProtocolErrorCode = 'bad_message' | 'room_not_found' | 'not_joined' | 'rate_limited' | 'wrong_room_kind';

export interface CreateRoomRequest {
  readonly kind?: 'planning' | 'retro';
  readonly deckId?: string;
  readonly title?: string;
  readonly mode?: 'anonymous' | 'visible';
}

export interface CreateRoomResponse {
  readonly roomId: string;
}

export interface RoomInfoResponse {
  readonly roomId: string;
  readonly exists: boolean;
  readonly kind: 'planning' | 'retro';
  readonly participantCount: number;
  /**
   * Required for both kinds for simplicity — a retro room reports `'none'`
   * rather than making every existing call site handle an optional field.
   */
  readonly deckId: string;
}
